import { useCart } from '@/hooks/useCart';
import { Box } from '@mui/material';
import CartSummaryLineItem from './CartSummaryLineItem';
import BuyCartIcon from '@/assets/cart/BuyCartIcon.svg';
import RentCartIcon from '@/assets/cart/RentCartIcon.svg';
import { ApiState, ViewStates } from '@/models/generics';
import CartSummaryShimmer from './Shimmer';
import { useRouter } from 'next/router';
import { cartService } from '@/services/cart';
import { RefObject, useEffect, useState } from 'react';

import { ActiveCartType } from '@/services/cart/models/update-cart';
import Auth from '@/components/Auth';
import { useLocation } from '@/hooks/useLocation';
import { dataLayerPushWithEvent, emitMoeEvent } from '@/utils/dataLayer';
import { pluralizeStringWithQuantity } from '@/utils/stringTransformations';
import { useLogin } from '@/hooks/useLogin';
import { CART_TYPE } from '@/services/cart/models/cart';
import { MinimumOrderValueDrawer } from './MinimumOrderValueDrawer';
import { useNotification } from '@/hooks/useNotification';
import { NotificationSeverity } from '../NotifyToast';
import { OOSCartItemsBulkActionDrawer } from '../Cart/OOSCartItemsBulkActionDrawer';
import MinTenureIcon from '@/assets/cart/minTenureCheckIcon.svg';
import HoverableButton from '@/atomic-components/Buttons/Hoverable';
import FlexBox from '@/atomic-components/FlexBox';
import TenureSelectorDrawer from '../TenureSelectorDrawer';
import { formatPrice } from '@/utils/common';
import { LencoTypography } from '@/atomic-components/LencoTypography';
import { TCreatePaymentPayload } from '@/services/cart/models/create-payment';
import { withFullScreenLoader } from '@/hoc/withFullScreenLoader';
import { useFullScreenLoader } from '@/hooks/useFullScreenLoader';

enum CART_PAGE_AUTH_STATE {
	OPEN = 'CART_PAGE_AUTH_STATE_OPEN',
	CLOSE = 'CART_PAGE_AUTH_STATE_CLOSE',
}

interface CartSummaryProps {
	onInfoClicked: (cartType: CART_TYPE) => void;
	openCartOosActionDrawer: () => void;
	closeCartOosActionDrawer: () => void;
	updateWhatsappConsentStatus: () => void;
	gstin: any;
}

const CartSummary = (props: any) => {
	const {
		getCartApiState,
		buyCart,
		rentCart,
		buyCartItemsCount,
		rentalCartItemsCount,
		cart,
		id: cartId,
		buyCartItems,
		rentalCartItems,
		getCartAttributes,
		cartActions,
	} = useCart();
	const [checkoutButtonViewState, setCheckoutButtonViewState] =
		useState<ApiState>(ApiState.IDLE);
	const { loader } = useFullScreenLoader();
	const { selectedLocation } = useLocation();
	const { isLoggedIn, loginActions } = useLogin();
	const [minOrderValueDrawerViewState, setMinOrderValueDrawerViewState] =
		useState<ViewStates>(ViewStates.NOT_VISIBLE);
	const [minTenureDrawerViewState, setMinTenureDrawerViewState] =
		useState<ViewStates>(ViewStates.NOT_VISIBLE);

	const [updateTenureViewState, setUpdateTenureViewState] = useState<ApiState>(
		ApiState.IDLE
	);
	const [bulkActionDrawerOpen, setBulkActionDrawerOpen] = useState(false);
	const [bulkActionDrawerData, setBulkActionDrawerData] = useState<any>();
	const {
		actions: { notifyUser },
	} = useNotification();

	const router = useRouter();

	function showBulkTenureSelector() {
		dataLayerPushWithEvent('moe_event', {
			moe_event_name: 'Cart Explore Payment Plans',
			moe_event_data: {
				screen_name: 'Cart Page',
				current_bulk_tenure_selected: rentCart?.tenureSelector?.selectedTenure,
				position: 'cta_switcher',
			},
		});

		setBulkActionDrawerData(rentCart?.tenureSelector);
		setBulkActionDrawerOpen(true);
	}
	function bulkActionDrawerClose() {
		setBulkActionDrawerOpen(false);
		setBulkActionDrawerData(undefined);
	}

	function emitOrderSummaryMoeEvent(eventName: string) {
		const {
			rentalProductIdsString,
			buyProductIdsString,
			cartStatus,
			lineOfProductsString,
		} = getCartAttributes();

		emitMoeEvent(eventName, {
			total_cart_value: cart?.total?.value,
			buy_cart_value: buyCart?.total?.value,
			rent_cart_value: rentCart?.total?.value,
			rent_products: rentalProductIdsString,
			buy_products: buyProductIdsString,
			cart_status: cartStatus,
			lineofproduct: lineOfProductsString,
		});
	}

	function bulkTenureUpdate(tenure: number, price?: number) {
		loader.show();
		setUpdateTenureViewState(ApiState.LOADING);

		if (price) {
			emitMoeEvent('Cart Bulk Tenure Switched', {
				price: price,
				old_bulk_tenure_selected: rentCart?.tenureSelector?.selectedTenure,
				new_bulk_tenure_selected: tenure,
			});
		}

		cartService
			.bulkTenureUpdate(cart?.id as number, { tenure: tenure })
			.then((res) => {
				if (res?.data?.success) {
					notifyUser({
						severity: NotificationSeverity.SUCCESS,
						message: 'Tenure updated successfully',
					});
					cartActions.getCart();
				}
			})
			.catch((err) => {
				notifyUser({
					severity: NotificationSeverity.ERROR,
					message: 'Failed to update tenure',
				});
				console.log(err);
			})
			.finally(() => {
				loader.hide();
				setUpdateTenureViewState(ApiState.COMPLETED);
				setBulkActionDrawerOpen(false);
			});
	}

	function getCartContext(): ActiveCartType {
		return 'ALL';
	}

	function emitProceedToCheckoutEvent() {
		const {
			rentalProductIdsString,
			buyProductIdsString,
			cartStatus,
			lineOfProductsString,
			verticalsString,
		} = getCartAttributes();

		emitMoeEvent('Proceed to Checkout', {
			total_cart_value: cart?.total?.value,
			buy_cart_value: buyCart?.total?.value,
			rent_cart_value: rentCart?.total?.value,
			rent_products: rentalProductIdsString,
			buy_products: buyProductIdsString,
			cart_status: cartStatus,
			lineofproduct: lineOfProductsString,
			vertical: verticalsString,
		});
		if (props?.gstin) {
			emitMoeEvent('Gst Entered', {
				cart_type: lineOfProductsString,
				vertical: verticalsString,
			});
		}
	}

	const cartShouldHaveMinimumOrderValue = () => {
		if (!cart?.hasPassedMov) {
			setMinOrderValueDrawerViewState(ViewStates.VISIBLE);
			throw Error('MOV_CHECK_FAILED');
		}
	};

	const cartShouldHaveMinimumTenure = () => {
		if (cart?.uiConfigurations?.shouldShowMinTenurePopup) {
			setMinTenureDrawerViewState(ViewStates.VISIBLE);
			dataLayerPushWithEvent('moe_event', {
				moe_event_name: 'Min Tenure Check Opened',
				moe_event_data: {
					screen_name: 'Cart Page',
				},
			});
			throw Error('MIN_TENURE_CHECK_FAILED');
		}
	};

	const userShouldBeLoggedIn = () => {
		if (!isLoggedIn) {
			loginActions.showLoginFlow();
			throw Error('NOT_LOGGED_IN');
		}
	};

	const doesCartContainsOosItems = () => {
		const containsOosItems =
			buyCartItems?.some((item) => item.isOutOfStock) ||
			rentalCartItems?.some((item) => item.isOutOfStock);

		return containsOosItems;
	};

	const cartShouldNotHaveOosItems = () => {
		if (doesCartContainsOosItems()) {
			props.openCartOosActionDrawer();
			throw Error('CONTAINS_OOS_ITEMS');
		}
	};

	async function ProccedToCheckout() {
		dataLayerPushWithEvent('moe_event', {
			moe_event_name: 'Min Tenure Check Proceeded',
			moe_event_data: {
				screen_name: 'Cart Page',
			},
		});

		if (cart?.canCheckout) {
			loader.show();

			props?.updateWhatsappConsentStatus();
			emitProceedToCheckoutEvent();
			setCheckoutButtonViewState(ApiState.LOADING);
			let isCartUpdated = false;

			let pathname = '/checkout/address';

			if (cart?.shippingAddressDetails?.pincode == selectedLocation?.pincode) {
				emitOrderSummaryMoeEvent('Proceed to Payment ');
				createPayment();
			} else {
				router.push({
					pathname,
					query: {
						cartContext: getCartContext(),
					},
				});
			}

			// loader.hide();
		} else {
			notifyUser({
				message: cart?.errorMessage,
				severity: NotificationSeverity.ERROR,
			});
		}
		// loader.hide();
	}
	const { actions } = useNotification();
	const [createPaymentViewState, setCreatePaymentViewState] =
		useState<ApiState>(ApiState.IDLE);
	const [isAutopayConsentViewed, setIsAutopayConsentViewed] = useState(false);

	const [autopayConsentOpen, setAutopayConsentOpen] = useState(false);

	const createPayment = () => {
		if (
			cart?.isEligibleForAutopay &&
			cart?.isAutopaySelected == false &&
			!isAutopayConsentViewed
		) {
			emitMoeEvent('Autopay_ACQ: Consent screen opened');
			setIsAutopayConsentViewed(true);
			setAutopayConsentOpen(true);
			return;
		}
		setCreatePaymentViewState(ApiState.LOADING);

		const payload: TCreatePaymentPayload = {
			gstin: props?.gstin,
			autopay: cart?.isAutopaySelected,
		};

		cartService
			.createPayment(cartId as number, payload)
			.then((res) => {
				if (res.data.success) {
					router.push({
						pathname: '/checkout/payment',
						query: { identifier: res.data?.data?.paymentDetails.id },
					});
				} else {
					actions.notifyUser({
						message: res.data.error.message,
						severity: NotificationSeverity.WARNING,
					});
				}
			})
			.catch((error) => {
				actions.notifyUser({
					message: error?.response?.data?.error?.message,
					severity: NotificationSeverity.ERROR,
				});
			})
			.finally(() => {
				setCreatePaymentViewState(ApiState.COMPLETED);
				loader.hide();
			});
	};

	async function handleCheckout() {
		try {
			props?.updateWhatsappConsentStatus();
			userShouldBeLoggedIn();
			cartShouldNotHaveOosItems();
			cartShouldHaveMinimumOrderValue();
			cartShouldHaveMinimumTenure();
			if (cart?.canCheckout) {
				loader.show();
				emitProceedToCheckoutEvent();
				setCheckoutButtonViewState(ApiState.LOADING);
				let isCartUpdated = false;

				let pathname = '/checkout/address';

				if (
					cart?.shippingAddressDetails?.pincode == selectedLocation?.pincode
				) {
					emitOrderSummaryMoeEvent('Proceed to Payment ');
					createPayment();
				} else {
					router.push({
						pathname,
						query: {
							cartContext: getCartContext(),
						},
					});
					loader.hide();
				}
			} else {
				notifyUser({
					message: cart?.errorMessage,
					severity: NotificationSeverity.ERROR,
				});
			}
		} catch (error) {
			console.error(error);
		}
	}

	// useEffect(() => {
	// 	if (isLoggedIn) {
	// 		getUserAddresses();
	// 	}
	// }, [isLoggedIn]);

	useEffect(() => {
		if (!doesCartContainsOosItems()) {
			props.closeCartOosActionDrawer();
		}
	}, [cart]);

	if (getCartApiState === ApiState.LOADING) {
		return <CartSummaryShimmer />;
	}
	const formattedValue = formatPrice(cart?.total?.displayValue as string);
	const formattedValueString = Array.isArray(formattedValue) ? (
		<>
			₹{formattedValue[0]}
			<LencoTypography
				component='span'
				sx={{ fontSize: '10px', fontWeight: 400 }}
			>
				.{formattedValue[1]}
			</LencoTypography>
		</>
	) : (
		(cart?.total?.displayValue as string)
	);
	return (
		<>
			<MinimumOrderValueDrawer
				open={minOrderValueDrawerViewState == ViewStates.VISIBLE}
				onClose={() => setMinOrderValueDrawerViewState(ViewStates.NOT_VISIBLE)}
				cta={cart?.movCheck?.failureCtx?.cta?.ctaLink}
				title={'<div>Minimum Order Value</div>'}
				description={
					cart?.movCheck?.failureCtx?.cta?.descriptionForWeb as string
				}
			/>
			<MinimumOrderValueDrawer
				open={minTenureDrawerViewState == ViewStates.VISIBLE}
				onClose={() => {
					setMinTenureDrawerViewState(ViewStates.NOT_VISIBLE);
					dataLayerPushWithEvent('moe_event', {
						moe_event_name: 'Min Tenure Check Dismissed',
						moe_event_data: {
							screen_name: 'Cart Page',
						},
					});
				}}
				cta={''}
				titleIcon={<MinTenureIcon />}
				subtitle={'<div>Rental Terms Reminder</div>'}
				// title={`<div>${<MinTenureIcon/>}</div>`}
				description={`${cart?.uiConfigurations?.minTenure?.info?.title}<br/>${cart?.uiConfigurations?.minTenure?.info?.description}`}
				onProceed={ProccedToCheckout}
				showProceedCta={true}
			/>
			<Box
				p='25px'
				sx={{
					background: 'white',
					borderRadius: '8px',
					boxShadow: '0px 8px 16px rgba(34, 34, 34, 0.1)',
				}}
			>
				{buyCartItemsCount > 0 && (
					<Box mb='16px'>
						<CartSummaryLineItem
							Icon={BuyCartIcon}
							title={'Buy'}
							subTitle={pluralizeStringWithQuantity(buyCartItemsCount, 'item')}
							amount={buyCart?.total.displayValue as string}
							onInfoClicked={() => props.onInfoClicked(CART_TYPE.BUY)}
						/>
					</Box>
				)}
				{rentalCartItemsCount > 0 && (
					<Box mb='16px'>
						<CartSummaryLineItem
							Icon={RentCartIcon}
							title={'Rent'}
							subTitle={pluralizeStringWithQuantity(
								rentalCartItemsCount,
								'item'
							)}
							selectedTenure={rentCart?.tenureSelector?.selectedTenure}
							upfrontOffer={rentCart?.tenureSelector?.upfrontPaymentPlans}
							amount={rentCart?.total.displayValue as string}
							onInfoClicked={() => props.onInfoClicked(CART_TYPE.RENT)}
							onTenureClicked={showBulkTenureSelector}
						/>
					</Box>
				)}
				<FlexBox>
					<HoverableButton
						isLoading={checkoutButtonViewState == ApiState.LOADING}
						onClick={handleCheckout}
						leftLabel={formattedValueString}
						rightLabel='PROCEED'
					/>
				</FlexBox>
			</Box>
			<TenureSelectorDrawer
				open={bulkActionDrawerOpen}
				onClose={bulkActionDrawerClose}
				onTenureChange={bulkTenureUpdate}
				currentTenure={rentCart?.tenureSelector?.selectedTenure}
				isUpdatingTenure={updateTenureViewState == ApiState.LOADING}
				upfrontOffers={{
					...rentCart?.tenureSelector,
					tenureSelected: {
						monthlyPaymentPlans: rentCart?.tenureSelector?.monthlyPaymentPlans,
						upfrontPaymentPlan: rentCart?.tenureSelector?.upfrontPaymentPlans,
					},
				}}
				screen='Cart'
				tenurePosition='bulk'
			/>
		</>
	);
};

export default CartSummary;
